import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { accountApi } from '../utils/api';
import { FileText, DollarSign, Coins, ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight } from 'lucide-react';

export default function Passbook() {
  const [activeTab, setActiveTab] = useState<'nt' | 'ms'>('nt');
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data: ntData, isLoading: ntLoading } = useQuery({
    queryKey: ['ledger', 'nt', page],
    queryFn: async () => {
      const response = await accountApi.getLedger({ limit, offset: (page - 1) * limit });
      return response.data;
    },
    enabled: activeTab === 'nt',
  });

  const { data: msData, isLoading: msLoading } = useQuery({
    queryKey: ['ledger', 'ms', page],
    queryFn: async () => {
      const response = await accountApi.getMsLedger({ limit, offset: (page - 1) * limit });
      return response.data;
    },
    enabled: activeTab === 'ms',
  });

  const currentData = activeTab === 'nt' ? ntData : msData;
  const isLoading = activeTab === 'nt' ? ntLoading : msLoading;
  const totalPages = Math.ceil((currentData?.total || 0) / limit);

  const getTypeConfig = (type: string) => {
    switch (type) {
      case 'deposit':
      case 'transfer_in':
        return { color: 'text-emerald-600', bg: 'bg-emerald-50', icon: ArrowDownRight, label: '存入' };
      case 'withdraw':
      case 'transfer_out':
      case 'fee':
      case 'shop':
        return { color: 'text-rose-600', bg: 'bg-rose-50', icon: ArrowUpRight, label: '支出' };
      case 'exchange':
        return { color: 'text-blue-600', bg: 'bg-blue-50', icon: ArrowUpRight, label: '兌換' };
      default:
        return { color: 'text-slate-600', bg: 'bg-slate-50', icon: ArrowUpRight, label: type };
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
          <FileText className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">電子存摺</h1>
          <p className="text-slate-500">查看您的交易記錄</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setActiveTab('nt'); setPage(1); }}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
            activeTab === 'nt'
              ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
        >
          <DollarSign className="h-5 w-5" />
          新臺幣存摺
        </button>
        <button
          onClick={() => { setActiveTab('ms'); setPage(1); }}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
            activeTab === 'ms'
              ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
        >
          <Coins className="h-5 w-5" />
          數資幣存摺
        </button>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-slate-500">載入中...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">日期時間</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">項目</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">類型</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-slate-600">金額</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-slate-600">餘額</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentData?.records?.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center text-slate-400">
                          <FileText className="h-12 w-12 mb-3 opacity-50" />
                          <p className="text-lg font-medium">暫無交易記錄</p>
                          <p className="text-sm">開始使用系統進行您的第一筆交易</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentData?.records?.map((record: any) => {
                      const config = getTypeConfig(record.type);
                      const Icon = config.icon;
                      return (
                        <tr key={record.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-6 py-4">
                            <p className="text-sm text-slate-900">{new Date(record.created_at).toLocaleDateString()}</p>
                            <p className="text-xs text-slate-400">{new Date(record.created_at).toLocaleTimeString()}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm font-medium text-slate-900">{record.reason}</p>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
                              <Icon className="h-3 w-3" />
                              {config.label}
                            </span>
                          </td>
                          <td className={`px-6 py-4 text-right text-sm font-bold ${config.color}`}>
                            {record.amount > 0 ? '+' : ''}
                            {activeTab === 'nt' 
                              ? record.amount.toLocaleString()
                              : record.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-slate-600">
                            {activeTab === 'nt'
                              ? record.balance_after.toLocaleString()
                              : record.balance_after.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                <p className="text-sm text-slate-500">
                  共 {currentData?.total || 0} 筆交易
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
